import { useState, type ChangeEvent, type FormEvent } from 'react';
import { getErrorMessage } from '../api/apiClient';
import { submitIntakeRequest } from '../api/intakeRequestsApi';
import type {
  BudgetRange,
  IntakeSubmissionResponse,
  ProjectTimeline,
  ProjectType,
  SubmitIntakeRequest,
} from '../types/intakeRequest';
import './Contact.css';

type IntakeRequestFormData = Omit<SubmitIntakeRequest, 'projectType' | 'timeline' | 'budgetRange'> & {
  projectType: ProjectType | '';
  timeline: ProjectTimeline | '';
  budgetRange: BudgetRange | '';
};

const initialForm: IntakeRequestFormData = {
  contactName: '',
  email: '',
  phone: '',
  organization: '',
  projectName: '',
  projectType: '',
  intention: '',
  scope: '',
  targetUsers: '',
  timeline: '',
  budgetRange: '',
  additionalContext: '',
};

function hasCompleteSelections(formData: IntakeRequestFormData): formData is SubmitIntakeRequest {
  return Boolean(formData.projectType && formData.timeline && formData.budgetRange);
}

function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [submission, setSubmission] = useState<IntakeSubmissionResponse | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    if (!hasCompleteSelections(formData)) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await submitIntakeRequest(formData);
      setSubmission(response);
      setFormData(initialForm);
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError, 'Your request could not be submitted.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submission) {
    return (
      <section className="contact intake-success" aria-labelledby="intake-success-title">
        <p className="contact-eyebrow">Request received</p>
        <h1 id="intake-success-title">Thank you for contacting Strata.</h1>
        <p>Your request reference is <strong>{submission.reference}</strong>.</p>
        <button type="button" className="submit-btn" onClick={() => setSubmission(null)}>
          Submit another request
        </button>
      </section>
    );
  }

  return (
    <section className="contact" aria-labelledby="intake-title">
      <header className="contact-header">
        <p className="contact-eyebrow">Project intake</p>
        <h1 id="intake-title">Tell us what you need to build.</h1>
        <p>Share the business goal, project scope, timing, and investment range.</p>
      </header>

      <form onSubmit={handleSubmit} className="intake-form">
        <fieldset>
          <legend>Contact</legend>
          <div className="intake-field-grid">
            <div className="form-group">
              <label htmlFor="contactName">Name</label>
              <input id="contactName" name="contactName" value={formData.contactName} onChange={handleChange} autoComplete="name" maxLength={120} required />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} autoComplete="email" maxLength={254} required />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} autoComplete="tel" maxLength={40} />
            </div>
            <div className="form-group">
              <label htmlFor="organization">Organization</label>
              <input id="organization" name="organization" value={formData.organization} onChange={handleChange} autoComplete="organization" maxLength={160} />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Project</legend>
          <div className="intake-field-grid">
            <div className="form-group">
              <label htmlFor="projectName">Project name</label>
              <input id="projectName" name="projectName" value={formData.projectName} onChange={handleChange} maxLength={160} required />
            </div>
            <div className="form-group">
              <label htmlFor="projectType">Project type</label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={(event) => setFormData((current) => ({
                  ...current,
                  projectType: event.target.value as ProjectType | '',
                }))}
                required
              >
                <option value="">Select a type</option>
                <option value="WEB_APPLICATION">Web application</option>
                <option value="MOBILE_APPLICATION">Mobile application</option>
                <option value="INTERNAL_SYSTEM">Internal business system</option>
                <option value="INTEGRATION">System integration</option>
                <option value="EXISTING_PRODUCT">Existing product improvement</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="form-group form-group-wide">
              <label htmlFor="intention">Business intention</label>
              <textarea id="intention" name="intention" rows={4} value={formData.intention} onChange={handleChange} maxLength={2000} placeholder="What outcome should this project create?" required />
            </div>
            <div className="form-group form-group-wide">
              <label htmlFor="scope">Expected scope</label>
              <textarea id="scope" name="scope" rows={5} value={formData.scope} onChange={handleChange} maxLength={5000} placeholder="Key features, workflows, and constraints." required />
            </div>
            <div className="form-group form-group-wide">
              <label htmlFor="targetUsers">Intended users</label>
              <textarea id="targetUsers" name="targetUsers" rows={3} value={formData.targetUsers} onChange={handleChange} maxLength={2000} placeholder="Who will use it, and in what context?" />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Delivery</legend>
          <div className="intake-field-grid">
            <div className="form-group">
              <label htmlFor="timeline">Target timeline</label>
              <select
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={(event) => setFormData((current) => ({
                  ...current,
                  timeline: event.target.value as ProjectTimeline | '',
                }))}
                required
              >
                <option value="">Select a timeline</option>
                <option value="UNDER_3_MONTHS">Under 3 months</option>
                <option value="THREE_TO_SIX_MONTHS">3 to 6 months</option>
                <option value="SIX_TO_TWELVE_MONTHS">6 to 12 months</option>
                <option value="OVER_TWELVE_MONTHS">More than 12 months</option>
                <option value="FLEXIBLE">Flexible</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="budgetRange">Budget range</label>
              <select
                id="budgetRange"
                name="budgetRange"
                value={formData.budgetRange}
                onChange={(event) => setFormData((current) => ({
                  ...current,
                  budgetRange: event.target.value as BudgetRange | '',
                }))}
                required
              >
                <option value="">Select a range</option>
                <option value="UNDER_2500">Under $2,500</option>
                <option value="BETWEEN_2500_AND_5000">$2,500 to $5,000</option>
                <option value="BETWEEN_5000_AND_10000">$5,000 to $10,000</option>
                <option value="BETWEEN_10000_AND_20000">$10,000 to $20,000</option>
                <option value="OVER_20000">More than $20,000</option>
                <option value="UNDETERMINED">Not sure yet</option>
              </select>
            </div>
            <div className="form-group form-group-wide">
              <label htmlFor="additionalContext">Additional context</label>
              <textarea id="additionalContext" name="additionalContext" rows={4} value={formData.additionalContext} onChange={handleChange} maxLength={5000} placeholder="Existing systems, risks, or relevant background." />
            </div>
          </div>
        </fieldset>

        {error && <p className="intake-error" role="alert">{error}</p>}
        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit request'}
        </button>
      </form>
    </section>
  );
}

export default Contact;
